import React, { useEffect, useState } from "react";
import { useAuth } from "../components/AuthContext";

const EDITION_ICONS = {
  public_cloud: "☁️",
  private_cloud: "🏢",
  on_premise: "🖥️",
  btp: "🔗"
};

export default function SAPSettings() {
  const { adminEmail, shipperEmail } = useAuth();
  const [config, setConfig] = useState(null);
  const [editions, setEditions] = useState([]);
  const [logs, setLogs] = useState([]);
  const [roleMappings, setRoleMappings] = useState([]);
  const [syncStatus, setSyncStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [activeTab, setActiveTab] = useState("connection");
  const [newMapping, setNewMapping] = useState({ sapRole: "", railoptixRole: "CARRIER" });

  const headers = {
    "x-admin-email": adminEmail || "",
    "x-user-role": "BROKER_ADMIN"
  };

  const [form, setForm] = useState({
    sap_edition: "public_cloud",
    sap_host: "",
    sap_client: "100",
    auth_type: "basic",
    sap_comm_user: "",
    sap_comm_password: "",
    sap_comm_scenario: "SAP_COM_0008",
    odata_base_url: "",
    soap_endpoint_url: "",
    bp_service_path: "/sap/opu/odata/sap/API_BUSINESS_PARTNER",
    // X.509
    x509_cert_path: "",
    x509_key_path: "",
    x509_ca_bundle: "",
    // OAuth2
    oauth2_token_url: "",
    oauth2_scope: "",
    // Replication
    replication_mode: "odata_poll",
    // IDoc
    idoc_partner_number: "",
    idoc_partner_type: "LS",
    idoc_message_type: "DEBMAS",
    // BTP
    btp_subaccount: "",
    btp_service_url: "",
    btp_destination_name: "",
    btp_event_mesh_topic: "",
    // General
    master_data_filter: "",
    enabled: false,
  });

  useEffect(() => {
    Promise.all([
      fetch("/api/sap/config", { headers }).then(r => r.ok ? r.json() : { config: null }),
      fetch("/api/sap/editions", { headers }).then(r => r.ok ? r.json() : { editions: [] }),
      fetch("/api/sap/sync-logs", { headers }).then(r => r.ok ? r.json() : { logs: [] }),
      fetch("/api/sap/sync-status", { headers }).then(r => r.ok ? r.json() : null),
      fetch("/api/sap/role-mappings", { headers }).then(r => r.ok ? r.json() : { mappings: [] }),
    ]).then(([configData, editionsData, logsData, statusData, mappingsData]) => {
      if (configData.config) {
        setConfig(configData.config);
        setForm(prev => ({ ...prev, ...configData.config }));
      }
      setEditions(editionsData.editions || []);
      setLogs(logsData.logs || []);
      setSyncStatus(statusData);
      setRoleMappings(mappingsData.mappings || []);
      setLoading(false);
    }).catch(err => {
      console.error("Failed to load SAP config:", err);
      setLoading(false);
    });
  }, []);

  const selectedEdition = editions.find(e => e.key === form.sap_edition);

  const saveConfig = async () => {
    setSaving(true);
    try {
      const method = config ? "PATCH" : "POST";
      const res = await fetch("/api/sap/config", {
        method,
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || "Save failed");
      alert("Configuration saved!");
      window.location.reload();
    } catch (e) { alert("Error: " + e.message); }
    finally { setSaving(false); }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetch("/api/sap/test-connection", { method: "POST", headers });
      setTestResult(await res.json());
    } catch (e) { setTestResult({ success: false, message: e.message }); }
    finally { setTesting(false); }
  };

  const triggerSync = async (bpNumber = null) => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await fetch("/api/sap/sync", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ bp_number: bpNumber, max_records: 100 })
      });
      setSyncResult(await res.json());
    } catch (e) { setSyncResult({ error: e.message }); }
    finally { setSyncing(false); }
  };

  const addRoleMapping = async () => {
    try {
      await fetch("/api/sap/role-mappings", {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ sap_bp_role: newMapping.sapRole, railoptix_role_key: newMapping.railoptixRole })
      });
      setNewMapping({ sapRole: "", railoptixRole: "CARRIER" });
      const res = await fetch("/api/sap/role-mappings", { headers });
      const data = await res.json();
      setRoleMappings(data.mappings || []);
    } catch (e) { alert("Error: " + e.message); }
  };

  const deleteRoleMapping = async (id) => {
    try {
      await fetch(`/api/sap/role-mappings/${id}`, { method: "DELETE", headers });
      setRoleMappings(roleMappings.filter(m => m.id !== id));
    } catch (e) { alert("Error: " + e.message); }
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center" }}>Loading SAP configuration...</div>;

  const tabs = [
    { id: "connection", label: "🔌 Connection" },
    { id: "auth", label: "🔐 Authentication" },
    { id: "replication", label: "🔄 Replication" },
    { id: "roles", label: "👥 Role Mapping" },
    { id: "sync", label: "📊 Sync & Logs" },
  ];

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: 20 }}>
      {/* Header */}
      <div style={{ background: "linear-gradient(135deg, #0f172a, #1e3a5f)", borderRadius: 14, padding: "24px 28px", marginBottom: 24, color: "white" }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 900 }}>⚙️ SAP Integration Hub</h1>
        <p style={{ margin: "6px 0 0", opacity: 0.7, fontSize: 14 }}>
          Connect to SAP S/4HANA, BTP, and Enterprise Services
        </p>
      </div>

      {/* Status Bar */}
      <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 12, padding: 16, marginBottom: 20, display: "flex", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <span style={{
          padding: "6px 16px", borderRadius: 20, fontWeight: 700, fontSize: 13,
          background: config?.enabled ? "#d1fae5" : "#f3f4f6",
          color: config?.enabled ? "#065f46" : "#6b7280"
        }}>
          {config?.enabled ? "✓ Connected" : "⚪ Not Configured"}
        </span>
        {config?.sap_edition && (
          <span style={{ padding: "4px 12px", background: "#dbeafe", color: "#1e40af", borderRadius: 12, fontSize: 12, fontWeight: 600 }}>
            {EDITION_ICONS[config.sap_edition]} {editions.find(e => e.key === config.sap_edition)?.name || config.sap_edition}
          </span>
        )}
        {syncStatus?.lastSync && (
          <span style={{ color: "#6b7280", fontSize: 13 }}>Last sync: {new Date(syncStatus.lastSync).toLocaleString()}</span>
        )}
        <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
          <button onClick={testConnection} disabled={testing || !config}
            style={{ padding: "8px 16px", background: testing ? "#9ca3af" : "#3b82f6", color: "white", border: "none", borderRadius: 8, fontWeight: 600, cursor: "pointer", fontSize: 13 }}>
            {testing ? "Testing..." : "🧪 Test Connection"}
          </button>
          <button onClick={saveConfig} disabled={saving}
            style={{ padding: "8px 16px", background: saving ? "#9ca3af" : "#dc2626", color: "white", border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer", fontSize: 13 }}>
            {saving ? "Saving..." : "💾 Save Config"}
          </button>
        </div>
      </div>

      {testResult && (
        <div style={{ marginBottom: 20, padding: 14, borderRadius: 10, background: testResult.success ? "#d1fae5" : "#fef2f2", color: testResult.success ? "#065f46" : "#dc2626", fontWeight: 600 }}>
          {testResult.success ? "✓ " : "✗ "}{testResult.message}
          {testResult.testSteps && (
            <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
              {testResult.testSteps.map((s, i) => (
                <span key={i} style={{ padding: "4px 10px", background: "rgba(255,255,255,0.6)", borderRadius: 6, fontSize: 12 }}>
                  {s.status === "success" ? "✓" : "✗"} {s.step} ({s.time})
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Edition Selection */}
      <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 12, padding: 20, marginBottom: 20 }}>
        <h3 style={{ margin: "0 0 14px", fontWeight: 800 }}>SAP Deployment Type</h3>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))", gap: 12 }}>
          {editions.map(ed => (
            <div key={ed.key}
              onClick={() => setForm({ ...form, sap_edition: ed.key, auth_type: ed.auth_options?.[0] || "basic" })}
              style={{
                padding: 16, borderRadius: 10, cursor: "pointer", transition: "all 0.15s",
                border: form.sap_edition === ed.key ? "2px solid #2563eb" : "2px solid #e5e7eb",
                background: form.sap_edition === ed.key ? "#eff6ff" : "white"
              }}>
              <div style={{ fontSize: 24, marginBottom: 6 }}>{EDITION_ICONS[ed.key]}</div>
              <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 4 }}>{ed.name}</div>
              <div style={{ fontSize: 12, color: "#6b7280", marginBottom: 8 }}>{ed.description}</div>
              <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
                {ed.auth_options?.map(a => (
                  <span key={a} style={{ padding: "2px 8px", background: "#f3f4f6", borderRadius: 4, fontSize: 10, fontWeight: 600, textTransform: "uppercase" }}>{a}</span>
                ))}
                {ed.supports_idoc && <span style={{ padding: "2px 8px", background: "#fef3c7", borderRadius: 4, fontSize: 10, fontWeight: 600 }}>IDoc</span>}
                {ed.supports_rfc && <span style={{ padding: "2px 8px", background: "#e0e7ff", borderRadius: 4, fontSize: 10, fontWeight: 600 }}>RFC</span>}
              </div>
            </div>
          ))}
        </div>
        {selectedEdition?.setup_notes && (
          <div style={{ marginTop: 12, padding: 12, background: "#fffbeb", borderRadius: 8, fontSize: 13, color: "#92400e" }}>
            💡 <strong>Setup:</strong> {selectedEdition.setup_notes}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 20 }}>
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            style={{
              padding: "10px 18px", border: "none", borderRadius: "8px 8px 0 0", fontWeight: 700, fontSize: 14, cursor: "pointer",
              background: activeTab === tab.id ? "#2563eb" : "#f3f4f6",
              color: activeTab === tab.id ? "white" : "#6b7280"
            }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* CONNECTION TAB */}
      {activeTab === "connection" && (
        <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 12, padding: 24 }}>
          <h3 style={{ margin: "0 0 16px", fontWeight: 800 }}>🔌 Connection Settings</h3>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, marginBottom: 16 }}>
            <div>
              <label style={lbl}>Host *</label>
              <input value={form.sap_host} onChange={e => setForm({ ...form, sap_host: e.target.value })}
                placeholder={form.sap_edition === "public_cloud" ? "myco.s4hana.ondemand.com" : form.sap_edition === "btp" ? "myco.btp.cloud.sap" : "sap.mycompany.com"}
                style={inp} />
            </div>
            <div>
              <label style={lbl}>Client</label>
              <input value={form.sap_client} onChange={e => setForm({ ...form, sap_client: e.target.value })} placeholder="100" style={inp} />
            </div>
            <div>
              <label style={lbl}>Communication Scenario</label>
              <select value={form.sap_comm_scenario} onChange={e => setForm({ ...form, sap_comm_scenario: e.target.value })} style={inp}>
                <option value="SAP_COM_0008">SAP_COM_0008 - Business Partner</option>
                <option value="SAP_COM_0414">SAP_COM_0414 - BP Integration</option>
                <option value="SAP_COM_0009">SAP_COM_0009 - Product Master</option>
                <option value="CUSTOM">Custom Service Path</option>
              </select>
            </div>
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
            <div>
              <label style={lbl}>OData Base URL</label>
              <input value={form.odata_base_url} onChange={e => setForm({ ...form, odata_base_url: e.target.value })}
                placeholder="/sap/opu/odata/sap/" style={inp} />
            </div>
            <div>
              <label style={lbl}>BP Service Path</label>
              <input value={form.bp_service_path} onChange={e => setForm({ ...form, bp_service_path: e.target.value })}
                placeholder="/sap/opu/odata/sap/API_BUSINESS_PARTNER" style={inp} />
            </div>
          </div>

          {form.sap_comm_scenario === "CUSTOM" && (
            <div>
              <label style={lbl}>Custom Service Path</label>
              <input value={form.customServicePath} onChange={e => setForm({ ...form, customServicePath: e.target.value })}
                placeholder="/sap/opu/odata/custom/..." style={inp} />
            </div>
          )}

          {/* BTP-specific fields */}
          {form.sap_edition === "btp" && (
            <div style={{ marginTop: 16, padding: 16, background: "#eff6ff", borderRadius: 10 }}>
              <h4 style={{ margin: "0 0 12px", fontWeight: 700 }}>🔗 BTP Configuration</h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 12 }}>
                <div>
                  <label style={lbl}>BTP Subaccount ID</label>
                  <input value={form.btp_subaccount} onChange={e => setForm({ ...form, btp_subaccount: e.target.value })}
                    placeholder="e.g., a1b2c3d4-e5f6-7890-abcd-ef1234567890" style={inp} />
                </div>
                <div>
                  <label style={lbl}>Destination Name</label>
                  <input value={form.btp_destination_name} onChange={e => setForm({ ...form, btp_destination_name: e.target.value })}
                    placeholder="S4HANA_PROD" style={inp} />
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div>
                  <label style={lbl}>Integration Suite Service URL</label>
                  <input value={form.btp_service_url} onChange={e => setForm({ ...form, btp_service_url: e.target.value })}
                    placeholder="https://myco.integrationsuite.cfapps.us10.hana.ondemand.com" style={inp} />
                </div>
                <div>
                  <label style={lbl}>Event Mesh Topic (optional)</label>
                  <input value={form.btp_event_mesh_topic} onChange={e => setForm({ ...form, btp_event_mesh_topic: e.target.value })}
                    placeholder="sap/s4/bp/BusinessPartner/Changed/v1" style={inp} />
                </div>
              </div>
            </div>
          )}

          {/* SOAP/IDoc endpoint for private cloud / on-premise */}
          {(form.sap_edition === "private_cloud" || form.sap_edition === "on_premise") && (
            <div style={{ marginTop: 16 }}>
              <label style={lbl}>SOAP Endpoint URL (optional)</label>
              <input value={form.soap_endpoint_url} onChange={e => setForm({ ...form, soap_endpoint_url: e.target.value })}
                placeholder="https://sap.mycompany.com:443/sap/bc/srt/scs_ext/..." style={inp} />
            </div>
          )}

          <div style={{ marginTop: 16, display: "flex", alignItems: "center", gap: 12 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
              <input type="checkbox" checked={form.enabled} onChange={e => setForm({ ...form, enabled: e.target.checked })}
                style={{ width: 20, height: 20 }} />
              <span style={{ fontWeight: 700, fontSize: 15, color: form.enabled ? "#059669" : "#6b7280" }}>
                {form.enabled ? "✅ Integration Enabled" : "Integration Disabled"}
              </span>
            </label>
          </div>
        </div>
      )}

      {/* AUTH TAB */}
      {activeTab === "auth" && (
        <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 12, padding: 24 }}>
          <h3 style={{ margin: "0 0 16px", fontWeight: 800 }}>🔐 Authentication</h3>

          <div style={{ display: "flex", gap: 10, marginBottom: 20 }}>
            {(selectedEdition?.auth_options || ["basic"]).map(auth => (
              <button key={auth} onClick={() => setForm({ ...form, auth_type: auth })}
                style={{
                  padding: "10px 20px", borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: "pointer",
                  border: form.auth_type === auth ? "none" : "2px solid #e5e7eb",
                  background: form.auth_type === auth ? "#2563eb" : "white",
                  color: form.auth_type === auth ? "white" : "#374151"
                }}>
                {auth === "basic" ? "🔑 Basic Auth" : auth === "oauth2" ? "🔒 OAuth 2.0" : "📜 X.509 Certificate"}
              </button>
            ))}
          </div>

          {form.auth_type === "basic" && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
              <div>
                <label style={lbl}>Communication User</label>
                <input value={form.sap_comm_user} onChange={e => setForm({ ...form, sap_comm_user: e.target.value })} style={inp} />
              </div>
              <div>
                <label style={lbl}>Password</label>
                <input type="password" value={form.sap_comm_password} onChange={e => setForm({ ...form, sap_comm_password: e.target.value })} style={inp} />
              </div>
            </div>
          )}

          {form.auth_type === "oauth2" && (
            <div style={{ display: "grid", gap: 16 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div>
                  <label style={lbl}>Token URL *</label>
                  <input value={form.oauth2_token_url} onChange={e => setForm({ ...form, oauth2_token_url: e.target.value })}
                    placeholder="https://...oauth/token" style={inp} />
                </div>
                <div>
                  <label style={lbl}>Scope</label>
                  <input value={form.oauth2_scope} onChange={e => setForm({ ...form, oauth2_scope: e.target.value })}
                    placeholder="API_BUSINESS_PARTNER_0001" style={inp} />
                </div>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div>
                  <label style={lbl}>Client ID</label>
                  <input value={form.oauthClientId || ""} onChange={e => setForm({ ...form, oauthClientId: e.target.value })} style={inp} />
                </div>
                <div>
                  <label style={lbl}>Client Secret</label>
                  <input type="password" value={form.oauthClientSecret || ""} onChange={e => setForm({ ...form, oauthClientSecret: e.target.value })} style={inp} />
                </div>
              </div>
            </div>
          )}

          {form.auth_type === "x509" && (
            <div style={{ display: "grid", gap: 16 }}>
              <div>
                <label style={lbl}>Certificate Path *</label>
                <input value={form.x509_cert_path} onChange={e => setForm({ ...form, x509_cert_path: e.target.value })}
                  placeholder="/path/to/client-cert.pem" style={inp} />
              </div>
              <div>
                <label style={lbl}>Private Key Path *</label>
                <input value={form.x509_key_path} onChange={e => setForm({ ...form, x509_key_path: e.target.value })}
                  placeholder="/path/to/client-key.pem" style={inp} />
              </div>
              <div>
                <label style={lbl}>CA Bundle (optional)</label>
                <input value={form.x509_ca_bundle} onChange={e => setForm({ ...form, x509_ca_bundle: e.target.value })}
                  placeholder="/path/to/ca-bundle.crt" style={inp} />
              </div>
            </div>
          )}
        </div>
      )}

      {/* REPLICATION TAB */}
      {activeTab === "replication" && (
        <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 12, padding: 24 }}>
          <h3 style={{ margin: "0 0 16px", fontWeight: 800 }}>🔄 Replication Mode</h3>

          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginBottom: 20 }}>
            {(selectedEdition?.replication_options || ["odata_poll"]).map(mode => {
              const modeInfo = {
                odata_poll: { icon: "🔄", name: "OData Polling", desc: "Pull data on schedule via OData API" },
                soap_push: { icon: "📤", name: "SOAP Push", desc: "SAP pushes changes via SOAP web service" },
                idoc_push: { icon: "📨", name: "IDoc Push", desc: "SAP sends IDocs for real-time replication" },
                event_mesh: { icon: "⚡", name: "Event Mesh", desc: "BTP Event Mesh for event-driven sync" },
                api_management: { icon: "🌐", name: "API Management", desc: "BTP API Management gateway" },
              };
              const info = modeInfo[mode] || { icon: "?", name: mode, desc: "" };
              return (
                <div key={mode} onClick={() => setForm({ ...form, replication_mode: mode })}
                  style={{
                    padding: 16, borderRadius: 10, cursor: "pointer",
                    border: form.replication_mode === mode ? "2px solid #2563eb" : "2px solid #e5e7eb",
                    background: form.replication_mode === mode ? "#eff6ff" : "white"
                  }}>
                  <div style={{ fontSize: 24, marginBottom: 4 }}>{info.icon}</div>
                  <div style={{ fontWeight: 700 }}>{info.name}</div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>{info.desc}</div>
                </div>
              );
            })}
          </div>

          {/* IDoc settings */}
          {form.replication_mode === "idoc_push" && (
            <div style={{ padding: 16, background: "#fefce8", borderRadius: 10 }}>
              <h4 style={{ margin: "0 0 12px", fontWeight: 700 }}>📨 IDoc Configuration</h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16 }}>
                <div>
                  <label style={lbl}>Partner Number</label>
                  <input value={form.idoc_partner_number} onChange={e => setForm({ ...form, idoc_partner_number: e.target.value })}
                    placeholder="ROADOPTIX" style={inp} />
                </div>
                <div>
                  <label style={lbl}>Partner Type</label>
                  <select value={form.idoc_partner_type} onChange={e => setForm({ ...form, idoc_partner_type: e.target.value })} style={inp}>
                    <option value="LS">LS - Logical System</option>
                    <option value="KU">KU - Customer</option>
                    <option value="LI">LI - Vendor</option>
                  </select>
                </div>
                <div>
                  <label style={lbl}>Message Type</label>
                  <select value={form.idoc_message_type} onChange={e => setForm({ ...form, idoc_message_type: e.target.value })} style={inp}>
                    <option value="DEBMAS">DEBMAS - Customer Master</option>
                    <option value="CREMAS">CREMAS - Vendor Master</option>
                    <option value="MATMAS">MATMAS - Material Master</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Event Mesh settings */}
          {form.replication_mode === "event_mesh" && (
            <div style={{ padding: 16, background: "#eff6ff", borderRadius: 10 }}>
              <h4 style={{ margin: "0 0 12px", fontWeight: 700 }}>⚡ Event Mesh Configuration</h4>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div>
                  <label style={lbl}>Event Topic</label>
                  <input value={form.btp_event_mesh_topic} onChange={e => setForm({ ...form, btp_event_mesh_topic: e.target.value })}
                    placeholder="sap/s4/bp/BusinessPartner/Changed/v1" style={inp} />
                </div>
                <div>
                  <label style={lbl}>Webhook Endpoint</label>
                  <input value={"https://api.roadoptix.com/webhooks/sap-events"} disabled style={{ ...inp, background: "#f3f4f6" }} />
                </div>
              </div>
            </div>
          )}

          <div style={{ marginTop: 16 }}>
            <label style={lbl}>Master Data Filter (optional)</label>
            <input value={form.master_data_filter} onChange={e => setForm({ ...form, master_data_filter: e.target.value })}
              placeholder="$filter=BusinessPartnerCategory eq '2'" style={inp} />
          </div>
        </div>
      )}

      {/* ROLE MAPPING TAB */}
      {activeTab === "roles" && (
        <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 12, padding: 24 }}>
          <h3 style={{ margin: "0 0 6px", fontWeight: 800 }}>👥 SAP BP Role → RoadOptix Role Mapping</h3>
          <p style={{ margin: "0 0 16px", fontSize: 13, color: "#6b7280" }}>
            Map SAP Business Partner roles to RoadOptix partner types. Custom Z-roles supported for Private Cloud and On-Premise.
          </p>

          {/* Default mappings */}
          <div style={{ marginBottom: 16, padding: 14, background: "#f9fafb", borderRadius: 10 }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Default Mappings (built-in)</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, fontSize: 13 }}>
              {[
                { sap: "FLCU00 (Customer)", ro: "SHIPPER" },
                { sap: "FLVN00 (Vendor)", ro: "CARRIER" },
                { sap: "FLVN01 (Subcontractor)", ro: "CARRIER" },
                { sap: "BPR001 (Freight Forwarder)", ro: "BROKER" },
              ].map(m => (
                <div key={m.sap} style={{ display: "flex", justifyContent: "space-between", padding: "6px 10px", background: "white", borderRadius: 6 }}>
                  <span style={{ color: "#6b7280" }}>{m.sap}</span>
                  <span style={{ fontWeight: 700 }}>→ {m.ro}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Custom mappings */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 8 }}>Custom Mappings</div>
            {roleMappings.length === 0 ? (
              <div style={{ padding: 16, textAlign: "center", color: "#9ca3af", background: "#f9fafb", borderRadius: 8 }}>No custom mappings defined</div>
            ) : (
              <div style={{ display: "grid", gap: 6 }}>
                {roleMappings.map(m => (
                  <div key={m.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "8px 12px", background: "#f9fafb", borderRadius: 8 }}>
                    <span><strong>{m.sap_bp_role}</strong> → {m.railoptix_role_key}</span>
                    <button onClick={() => deleteRoleMapping(m.id)} style={{ background: "none", border: "none", color: "#dc2626", cursor: "pointer", fontWeight: 700 }}>✕</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Add new mapping */}
          {selectedEdition?.supports_custom_roles && (
            <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
              <div style={{ flex: 1 }}>
                <label style={lbl}>SAP BP Role (e.g., ZCARRIER)</label>
                <input value={newMapping.sapRole} onChange={e => setNewMapping({ ...newMapping, sapRole: e.target.value })} style={inp} />
              </div>
              <div style={{ flex: 1 }}>
                <label style={lbl}>RoadOptix Role</label>
                <select value={newMapping.railoptixRole} onChange={e => setNewMapping({ ...newMapping, railoptixRole: e.target.value })} style={inp}>
                  <option value="CARRIER">CARRIER</option>
                  <option value="SHIPPER">SHIPPER</option>
                  <option value="BROKER">BROKER</option>
                </select>
              </div>
              <button onClick={addRoleMapping} disabled={!newMapping.sapRole}
                style={{ padding: "10px 18px", background: "#2563eb", color: "white", border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}>
                + Add
              </button>
            </div>
          )}
          {!selectedEdition?.supports_custom_roles && (
            <div style={{ padding: 12, background: "#fffbeb", borderRadius: 8, fontSize: 13, color: "#92400e" }}>
              ⚠️ Custom role mappings require Private Cloud or On-Premise edition
            </div>
          )}
        </div>
      )}

      {/* SYNC TAB */}
      {activeTab === "sync" && (
        <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 12, padding: 24 }}>
          <h3 style={{ margin: "0 0 16px", fontWeight: 800 }}>📊 Sync & Activity</h3>

          {/* Sync controls */}
          <div style={{ display: "flex", gap: 12, marginBottom: 20 }}>
            <button onClick={() => triggerSync()} disabled={syncing || !config?.enabled}
              style={{ padding: "10px 20px", background: syncing ? "#9ca3af" : "#059669", color: "white", border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}>
              {syncing ? "⏳ Syncing..." : "🔄 Sync All Business Partners"}
            </button>
          </div>

          {syncResult && (
            <div style={{ padding: 14, background: syncResult.error ? "#fef2f2" : "#d1fae5", borderRadius: 10, marginBottom: 16, fontSize: 14 }}>
              {syncResult.error ? "❌ " + syncResult.error : `✅ Synced ${syncResult.synced || 0} records. ${syncResult.errors || 0} errors.`}
            </div>
          )}

          {/* Sync status */}
          {syncStatus && (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 12, marginBottom: 20 }}>
              {[
                { label: "Total Synced", value: syncStatus.totalSynced || 0, color: "#2563eb" },
                { label: "Last Run", value: syncStatus.lastSync ? new Date(syncStatus.lastSync).toLocaleDateString() : "Never", color: "#059669" },
                { label: "Errors", value: syncStatus.errors || 0, color: "#dc2626" },
                { label: "Next Scheduled", value: syncStatus.nextScheduled || "Manual", color: "#6b7280" },
              ].map(s => (
                <div key={s.label} style={{ padding: 14, background: "#f9fafb", borderRadius: 10, textAlign: "center" }}>
                  <div style={{ fontSize: 24, fontWeight: 900, color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>{s.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Sync logs */}
          <h4 style={{ margin: "0 0 10px", fontWeight: 700, fontSize: 14 }}>Recent Activity</h4>
          {logs.length === 0 ? (
            <div style={{ padding: 20, textAlign: "center", color: "#9ca3af" }}>No sync activity yet</div>
          ) : (
            <div style={{ maxHeight: 300, overflow: "auto", border: "1px solid #e5e7eb", borderRadius: 8 }}>
              {logs.map((log, i) => (
                <div key={i} style={{ padding: "10px 14px", borderBottom: "1px solid #f3f4f6", fontSize: 13, display: "flex", justifyContent: "space-between" }}>
                  <span>
                    <span style={{ color: log.status === "success" ? "#10b981" : "#ef4444" }}>●</span> {log.action}
                    {log.details && <span style={{ color: "#9ca3af", marginLeft: 8 }}>{log.details}</span>}
                  </span>
                  <span style={{ color: "#9ca3af", whiteSpace: "nowrap" }}>{log.timestamp ? new Date(log.timestamp).toLocaleString() : ""}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Integration Guide */}
      <div style={{ marginTop: 24, background: "#fffbeb", border: "1px solid #fef08a", borderRadius: 12, padding: 20 }}>
        <h3 style={{ margin: "0 0 12px 0", color: "#a16207", fontWeight: 800 }}>🚀 Integration Guide</h3>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, fontSize: 13, color: "#92400e" }}>
          <div>
            <strong>S/4HANA Public Cloud</strong>
            <ul style={{ margin: "4px 0", paddingLeft: 18, lineHeight: 1.8 }}>
              <li>Activate scope item 6W1</li>
              <li>Create Communication Arrangement (SAP_COM_0008)</li>
              <li>Use OAuth 2.0 or Basic Auth</li>
            </ul>
          </div>
          <div>
            <strong>S/4HANA Private Cloud</strong>
            <ul style={{ margin: "4px 0", paddingLeft: 18, lineHeight: 1.8 }}>
              <li>Register API_BUSINESS_PARTNER via /IWFND/MAINT_SERVICE</li>
              <li>Supports OData, SOAP, IDoc, and RFC</li>
              <li>X.509 certificate auth available</li>
            </ul>
          </div>
          <div>
            <strong>S/4HANA On-Premise</strong>
            <ul style={{ margin: "4px 0", paddingLeft: 18, lineHeight: 1.8 }}>
              <li>Configure SAP Gateway and ICF services</li>
              <li>Set up RFC destinations for IDoc</li>
              <li>All auth methods supported</li>
            </ul>
          </div>
          <div>
            <strong>SAP BTP</strong>
            <ul style={{ margin: "4px 0", paddingLeft: 18, lineHeight: 1.8 }}>
              <li>Create service instance in BTP Cockpit</li>
              <li>Configure Destination to S/4HANA backend</li>
              <li>Use Integration Suite or Event Mesh</li>
            </ul>
          </div>
        </div>
        <div style={{ marginTop: 12, fontSize: 13 }}>
          📧 Contact <strong>Symple Code</strong> for white-glove setup assistance
        </div>
      </div>
    </div>
  );
}

const lbl = { fontWeight: 600, display: "block", marginBottom: 4, fontSize: 13 };
const inp = { width: "100%", padding: 10, borderRadius: 8, border: "1px solid #d1d5db", fontSize: 14, boxSizing: "border-box" };
